import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Empty, Form, Input, InputNumber, Radio, Select, Space, Tag } from 'antd';
import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Table, { ColumnsType } from 'antd/es/table';
import { PlusOutlined, FilePdfOutlined, FileExcelOutlined, FileTextOutlined } from '@ant-design/icons';
import { Box, Button as MuiButton, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { Close, DeleteOutline, ArrowUpward, ArrowDownward, EditOutlined, Save, SubdirectoryArrowRight } from '@mui/icons-material';
import TableSearchInput from '../../Common/TableSearchInput';
import TableFilterSelect from '../../Common/TableFilterSelect';
import ExportMenuButton from '../../Common/ExportMenuButton';
import InputForm from '../../InputT';
import Swal from 'sweetalert2';
import {
    AgenceRef, ChampRef, GroupeNode, OperateurRef, RegleQuery, RegleRow, SaveResult, SimuResult, exprCondition
} from './ConditionsOperations';

dayjs.extend(customParseFormat); // requis pour dayjs(str, 'DD/MM/YYYY')

// ============================================================================
// Brouillon du constructeur : liste plate d'elements + operateurs entre eux.
// ops[i] relie elements[i] -> elements[i+1] (longueur = elements.length - 1).
// A l'enregistrement, la liste est normalisee en arbre a logique unique :
// ET est prioritaire sur OU, donc "A ET B OU C" -> OU[ ET[A,B], C ].
// ============================================================================
type Op = 'ET' | 'OU';

interface InlineCond {
    CHAMPEV?: string;
    OPECOMP?: string;
    VALEUR1?: string;
    VALEUR2?: string;
    LISTEVAL?: string[];
}
interface ElemDraft {
    _id: number;
    kind: 'COND' | 'REFC' | 'REFG' | 'SOUS';
    cond?: InlineCond;        // COND : condition saisie inline
    CODCOND?: string;         // REFC : condition existante
    IDGRPFILS?: number;       // REFG : groupe existant
    fils?: FlatDraft;         // SOUS : sous-groupe anonyme
}
interface FlatDraft {
    elements: ElemDraft[];
    ops: Op[];
    idgrp?: number;   // IDGRP du fils anonyme persiste (edition : update en place)
}
let _nextId = 1;
const nextId = () => _nextId++;
const emptyFlat = (): FlatDraft => ({ elements: [], ops: [] });

// Arbre normalise : noeud logique (L + enfants K) ou feuille (E = element)
type Norm = { L: Op; K: Norm[] } | { E: ElemDraft };
const norm = (d: FlatDraft): Norm | null => {
    const els = d.elements;
    if (els.length === 0) return null;
    if (els.length === 1) return { E: els[0] };
    const same = (o: Op) => d.ops.every(x => x === o);
    if (same('ET')) return { L: 'ET', K: els.map(e => ({ E: e })) };
    // Segments ET separes par OU -> racine OU (couvre aussi "tout OU" :
    // chaque segment est alors une feuille).
    const segs: ElemDraft[][] = [[els[0]]];
    d.ops.forEach((op, i) => {
        if (op === 'OU') segs.push([]);
        segs[segs.length - 1].push(els[i + 1]);
    });
    return {
        L: 'OU',
        K: segs.map(s => s.length === 1 ? { E: s[0] } : { L: 'ET' as Op, K: s.map(e => ({ E: e })) }),
    };
};

interface Tab1Props {
    form: FormInstance;
    rules: RegleRow[];
    columns: ColumnsType<RegleRow>;
    selectedKey: string | null;
    onSelectRow: (row: RegleRow | null) => void;
    selectedRule: RegleRow | null;
    groupeTree: GroupeNode | null;
    detailLoading: boolean;
    conditions: any[];
    groupes: any[];
    champs: ChampRef[];
    operateurs: OperateurRef[];
    agences: AgenceRef[];
    profMax: number;
    utilis: string;
    // Infos de l'utilisateur connecte pour l'en-tete des exports
    codage: string;
    agence: string;
    nomuti: string;
    loading: boolean;
    modalOpen: boolean;
    editingRule: RegleRow | null;
    editingTree: GroupeNode | null;
    onCloseModal: () => void;
    // Point d'entree unique : le backend determine le type final
    // a partir du contenu de DEFINITION (ELEMENTS discrimines).
    onSaveDefinition: (payload: any, isEdit: boolean) => Promise<SaveResult>;
    // Validation a blanc par la procedure stockee (aucune ecriture) :
    // appelee des les modales de saisie, la verification complete a
    // l'enregistrement reste inchangee.
    onPrevalider?: (c: InlineCond) => Promise<{ ok: boolean; message?: string }>;
    // Simulateur : evalue la definition courante avec des valeurs de test
    // (meme verdict par feuille que le moteur d'execution).
    onSimuler?: (def: any, valeurs: Record<string, string>) => Promise<SimuResult>;
    // Picklist "Valeur" : domaine resolu cote serveur par CODCHAMP
    // (table vide = champ sans domaine -> saisie typee).
    onReadDomaine?: (codchamp: string) => Promise<{ value: string; label: string }[]>;
    // Pagination serveur : la page courante + filtres viennent du parent,
    // qui recharge via ReadRegles.
    total: number;
    pageSize: number;
    query: RegleQuery;
    onQuery: (q: RegleQuery) => void;
    // Export : delegue au backend (OpeCatalogue/ExportRegles, pipeline
    // FastReport partage) — le parent telecharge le fichier retourne.
    onExport: (fmt: string) => Promise<void>;
}

// Contexte partage par les composants du constructeur
interface BuilderCtx {
    conditions: any[];
    groupes: any[];
    champs: ChampRef[];
    operateurs: OperateurRef[];
    agences: AgenceRef[];
    profMax: number;
    excludeIdgrp?: number;
    onPrevalider?: (c: InlineCond) => Promise<{ ok: boolean; message?: string }>;
    onReadDomaine?: (codchamp: string) => Promise<{ value: string; label: string }[]>;
}

const exprInline = (c: InlineCond, ops: OperateurRef[]): string => {
    if (!c.CHAMPEV || !c.OPECOMP) return '…';
    return exprCondition({ ...c, LISTEVAL: (c.LISTEVAL || []).join(';') }, ops);
};

// Expression lisible d'un element du brouillon
const elemExpr = (e: ElemDraft, ctx: BuilderCtx): string => {
    if (e.kind === 'COND') return e.cond ? exprInline(e.cond, ctx.operateurs) : '…';
    if (e.kind === 'REFC') {
        const c = ctx.conditions.find(x => x.CODCOND === e.CODCOND);
        return c ? exprCondition(c, ctx.operateurs) : (e.CODCOND || '…');
    }
    if (e.kind === 'REFG') {
        const g = ctx.groupes.find(x => x.IDGRP === e.IDGRPFILS);
        return g ? `«${g.CODGRP}»` : (e.IDGRPFILS ? `«#${e.IDGRPFILS}»` : '…');
    }
    return e.fils ? flatExpr(e.fils, ctx) : '(…)';
};

const normExpr = (n: Norm, ctx: BuilderCtx): string =>
    'E' in n ? elemExpr(n.E, ctx) : `(${n.K.map(k => normExpr(k, ctx)).join(` ${n.L} `)})`;

const flatExpr = (d: FlatDraft, ctx: BuilderCtx): string => {
    const n = norm(d);
    return n ? normExpr(n, ctx) : '';
};

// Convertit l'arbre backend (ReadGroupe) en brouillon plat : la LOGIQUE du
// niveau devient les operateurs entre elements, les sous-groupes nommes
// restent des references (REFG) et les fils anonymes des SOUS.
const treeToFlat = (n: GroupeNode): FlatDraft => ({
    elements: n.ELEMENTS.map(e => {
        if (e.FILS && e.FILS.CODGRP) return { _id: nextId(), kind: 'REFG' as const, IDGRPFILS: e.IDGRPFILS! };
        if (e.FILS) return { _id: nextId(), kind: 'SOUS' as const, fils: { ...treeToFlat(e.FILS), idgrp: e.FILS.IDGRP } };
        if (e.CODCOND) return { _id: nextId(), kind: 'REFC' as const, CODCOND: e.CODCOND };
        if ((e as any).CHAMPEV) return { _id: nextId(), kind: 'COND' as const, cond: { CHAMPEV: (e as any).CHAMPEV, OPECOMP: (e as any).OPECOMP, VALEUR1: (e as any).VALEUR1, VALEUR2: (e as any).VALEUR2, LISTEVAL: ((e as any).LISTEVAL || '').split(';').filter(Boolean) } };
        return { _id: nextId(), kind: 'REFG' as const, IDGRPFILS: e.IDGRPFILS! };
    }),
    ops: Array(Math.max(0, n.ELEMENTS.length - 1)).fill(n.LOGIQUE) as Op[],
});

// ============================ Simulateur ============================
// Sous l'aperçu d'expression : l'utilisateur saisit une valeur par champ
// evalue puis obtient le verdict par feuille (meme regles que le moteur :
// 1 vrai / 0 faux / -1 inevaluable, agregation ternaire ET/OU).
const SimuBlock = (p: {
    definition: any | null;
    draft: FlatDraft;
    ctx: BuilderCtx;
    onSimuler?: (def: any, valeurs: Record<string, string>) => Promise<SimuResult>;
}) => {
    const [valeurs, setValeurs] = useState<Record<string, string>>({});
    const [extra, setExtra] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState<SimuResult | null>(null);

    // Champs detectes dans le brouillon : inline + conditions referencees.
    // Les champs internes des GROUPES references ne sont pas connus cote
    // front : l'utilisateur peut les ajouter manuellement ci-dessous.
    const detectes = useMemo(() => {
        const acc = new Set<string>();
        const walk = (d: FlatDraft) => d.elements.forEach(e => {
            if (e.kind === 'COND' && e.cond?.CHAMPEV) acc.add(e.cond.CHAMPEV);
            else if (e.kind === 'REFC' && e.CODCOND) {
                const c = p.ctx.conditions.find(x => x.CODCOND === e.CODCOND);
                if (c?.CHAMPEV) acc.add(c.CHAMPEV);
            } else if (e.kind === 'SOUS' && e.fils) walk(e.fils);
        });
        walk(p.draft);
        return [...acc];
    }, [p.draft, p.ctx.conditions]); // eslint-disable-line react-hooks/exhaustive-deps

    const champsList = [...new Set([...detectes, ...extra])]
        .filter(c => p.ctx.champs.some(x => x.CODCHAMP === c));
    const restants = p.ctx.champs.filter(c => !champsList.includes(c.CODCHAMP));

    const setVal = (cod: string, v: any) =>
        setValeurs(prev => ({ ...prev, [cod]: v == null ? '' : String(v) }));

    // Picklists du simulateur : domaines resolus cote serveur, charges
    // uniquement pour les champs presents dans la definition.
    const [domaines, setDomaines] = useState<Record<string, { value: string; label: string }[]>>({});
    useEffect(() => {
        if (!p.ctx.onReadDomaine) return;
        champsList.forEach(c => {
            if (!(c in domaines))
                p.ctx.onReadDomaine!(c).then(o => setDomaines(d => ({ ...d, [c]: o })));
        });
    }, [champsList.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

    const inputFor = (cod: string) => {
        const ch = p.ctx.champs.find(c => c.CODCHAMP === cod);
        const dom = domaines[cod];
        if (dom?.length)
            return <Select size="small" showSearch optionFilterProp="label" style={{ width: '100%' }}
                placeholder="Choisir…" value={valeurs[cod] || undefined}
                options={dom}
                onChange={v => setVal(cod, v)} />;
        if (ch?.TYPDON === 'NUMERIC')
            return <InputNumber size="small" style={{ width: '100%' }}
                value={valeurs[cod] ? Number(valeurs[cod]) : undefined}
                onChange={v => setVal(cod, v)} />;
        if (ch?.TYPDON === 'DATE')
            return <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY"
                popupStyle={{ zIndex: 99999 }}
                value={valeurs[cod] ? dayjs(valeurs[cod], 'DD/MM/YYYY') : null}
                onChange={d => setVal(cod, d ? d.format('DD/MM/YYYY') : '')} />;
        return <Input size="small" value={valeurs[cod] || ''}
            onChange={e => setVal(cod, e.target.value)} />;
    };

    const run = async () => {
        if (!p.onSimuler || !p.definition) return;
        setBusy(true);
        setRes(await p.onSimuler(p.definition, valeurs));
        setBusy(false);
    };

    if (!p.onSimuler || !p.definition) return null;
    const badge = (r: number) =>
        r === 1 ? <Tag color="success">VRAI</Tag>
            : r === 0 ? <Tag color="error">FAUX</Tag>
                : <Tag color="warning">INÉVALUABLE</Tag>;

    return (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed var(--primaryColor)' }}>
            <Typography variant="caption" color="text.secondary">
                Simuler — saisissez une valeur par champ évalué
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {champsList.map(cod => {
                    const ch = p.ctx.champs.find(c => c.CODCHAMP === cod);
                    return (
                        <Box key={cod} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" title={`${cod}${ch?.LIBCHAMP ? ` — ${ch.LIBCHAMP}` : ''}`}
                                sx={{
                                    width: 330, flexShrink: 0, fontFamily: 'monospace',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                }}>
                                {cod}{ch?.LIBCHAMP ? ` — ${ch.LIBCHAMP}` : ''}
                            </Typography>
                            <Box sx={{ flex: 1, maxWidth: 320 }}>{inputFor(cod)}</Box>
                            {extra.includes(cod) && (
                                <IconButton size="small" onClick={() => setExtra(extra.filter(x => x !== cod))}>
                                    <Close sx={{ fontSize: 14 }} />
                                </IconButton>
                            )}
                        </Box>
                    );
                })}
            </Stack>
            <Space wrap style={{ marginTop: 6 }}>
                {restants.length > 0 && (
                    <Select size="small" style={{ width: 260 }} placeholder="+ Champ d'un groupe référencé…"
                        key={extra.length}    /* remount => placeholder reaffiche apres ajout */
                        options={restants.map(c => ({ value: c.CODCHAMP, label: `${c.CODCHAMP} — ${c.LIBCHAMP}` }))}
                        onChange={v => setExtra([...extra, v])} />
                )}
                {/* fond bleu primaire => texte blanc explicite, sinon le
                    libelle herite la couleur primaire (bouton "tout bleu") */}
                <Button size="small" type="primary" loading={busy} onClick={run}
                    style={{ color: '#fff' }}>Simuler</Button>
            </Space>
            {res && !res.ok && <Alert type="error" message={res.message} showIcon style={{ marginTop: 8 }} />}
            {res?.ok && (
                <Box sx={{ mt: 1 }}>
                    <Space align="center" wrap>
                        <b>Résultat :</b>
                        {res.resultat === 'VRAI' ? <Tag color="success"><b>VRAI</b></Tag>
                            : res.resultat === 'FAUX' ? <Tag color="error"><b>FAUX</b></Tag>
                                : <Tag color="warning"><b>INÉVALUABLE</b></Tag>}
                    </Space>
                    {res.detail && <Alert type="warning" message={res.detail} showIcon style={{ marginTop: 6 }} />}
                    {(res.snap || []).map((f, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
                            <Box sx={{ width: 92, flexShrink: 0, textAlign: 'center' }}>{badge(f.RES)}</Box>
                            <code style={{ fontSize: '0.78rem', color: 'var(--darkColor)' }}>{f.EXPR}</code>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

// ============================ Modale "Condition simple" ============================
// Saisie d'une condition inline : champ, operateur (filtre par TYPDON du
// champ) et valeur(s) adaptee(s) a l'operateur et au type de donnee.
const CondModal = (p: {
    open: boolean;
    initial?: InlineCond;
    champs: ChampRef[];
    operateurs: OperateurRef[];
    onReadDomaine?: (codchamp: string) => Promise<{ value: string; label: string }[]>;
    onPrevalider?: (c: InlineCond) => Promise<{ ok: boolean; message?: string }>;
    onClose: () => void;
    onOk: (c: InlineCond) => void;
}) => {
    const [f] = Form.useForm();
    const [checking, setChecking] = useState(false);
    const [errSp, setErrSp] = useState<string | null>(null);
    const champSel = Form.useWatch('CHAMPEV', f);
    const opeSel = Form.useWatch('OPECOMP', f);
    const champ = p.champs.find(c => c.CODCHAMP === champSel);
    const op = p.operateurs.find(o => o.CODOPECOMP === opeSel);
    const isListe = op?.REQUIERT_LISTE === true || op?.REQUIERT_LISTE === 1;
    const isVal2 = op?.REQUIERT_VAL2 === true || op?.REQUIERT_VAL2 === 1;
    // TYPDON_APPLIQ peut etre une liste ('NUMERIC,DATE' pour ENTRE).
    const opsPossibles = p.operateurs.filter(o =>
        !champ || o.TYPDON_APPLIQ === 'TOUS'
        || o.TYPDON_APPLIQ.split(',').map(a => a.trim()).includes(champ.TYPDON));

    useEffect(() => {
        if (!p.open) return;
        setErrSp(null);
        f.resetFields();
        if (p.initial) {
            const init: any = { ...p.initial };
            // DATE : les valeurs stockees 'JJ/MM/AAAA' redeviennent des
            // dayjs pour le DatePicker.
            if (p.champs.find(c => c.CODCHAMP === init.CHAMPEV)?.TYPDON === 'DATE') {
                if (init.VALEUR1) init.VALEUR1 = dayjs(init.VALEUR1, 'DD/MM/YYYY');
                if (init.VALEUR2) init.VALEUR2 = dayjs(init.VALEUR2, 'DD/MM/YYYY');
            }
            f.setFieldsValue(init);
        }
    }, [p.open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Picklist "Valeur" : le domaine du champ evalue est resolu cote
    // serveur (ReadDomaine). Table vide / champ sans domaine -> la
    // saisie typee (nombre/date/texte libre) est conservee.
    const [domainOpts, setDomainOpts] = useState<{ value: string; label: string }[]>([]);
    useEffect(() => {
        let live = true;
        if (champSel && p.onReadDomaine)
            p.onReadDomaine(champSel).then(o => { if (live) setDomainOpts(o); });
        else setDomainOpts([]);
        return () => { live = false; };
    }, [champSel]); // eslint-disable-line react-hooks/exhaustive-deps
    const hasDomaine = domainOpts.length > 0;

    // Composant de saisie selon le type de donnee du champ
    const valeurInput = (name: string, label: string) => (
        <Form.Item name={name} label={label} rules={[{ required: true, message: `${label} requis` }]}>
            {hasDomaine
                ? <Select showSearch optionFilterProp="label" placeholder="Choisir…"
                    options={domainOpts} />
                : champ?.TYPDON === 'NUMERIC'
                    ? <InputNumber style={{ width: '100%' }} />
                    : champ?.TYPDON === 'DATE'
                        // popupStyle : le calendrier doit passer au-dessus du
                        // Dialog MUI (z-index 1300) — seul .ant-select-dropdown
                        // est releve globalement, pas .ant-picker-dropdown.
                        ? <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                            popupStyle={{ zIndex: 99999 }} />
                        : <Input />}
        </Form.Item>
    );

    const ok = async () => {
        try {
            const v = await f.validateFields();
            // DATE : le DatePicker rend un dayjs -> 'JJ/MM/AAAA' (format
            // attendu par la procedure stockee, style 103).
            const fmt = (x: any) => x == null ? null
                : dayjs.isDayjs(x) ? x.format('DD/MM/YYYY') : x;
            const cond: InlineCond = {
                CHAMPEV: v.CHAMPEV,
                OPECOMP: v.OPECOMP,
                VALEUR1: isListe ? null : fmt(v.VALEUR1)?.toString(),
                VALEUR2: isVal2 ? fmt(v.VALEUR2)?.toString() : null,
                LISTEVAL: isListe ? (v.LISTEVAL || []) : [],
            };
            // Verification a blanc par la procedure stockee : la modale
            // reste ouverte sur un refus, rien n'est encore enregistre.
            if (p.onPrevalider) {
                setChecking(true);
                const r = await p.onPrevalider(cond);
                setChecking(false);
                if (!r.ok) { setErrSp(r.message || 'Condition invalide'); return; }
            }
            p.onOk(cond);
        } catch { /* champs invalides : la modale reste ouverte */ }
    };

    return (
        <Dialog open={p.open} onClose={p.onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{
                bgcolor: 'var(--primaryColor)', color: 'white', py: 1.5,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <b>Condition simple</b>
                <IconButton onClick={p.onClose} size="small" sx={{ color: 'white' }}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Form form={f} layout="vertical">
                    <Form.Item name="CHAMPEV" label="Champ évalué" rules={[{ required: true, message: 'Champ requis' }]}>
                        <Select showSearch optionFilterProp="label" placeholder="Choisir un champ…"
                            options={p.champs.map(c => ({ value: c.CODCHAMP, label: `${c.CODCHAMP} — ${c.LIBCHAMP}` }))}
                            onChange={() => f.setFieldsValue({ OPECOMP: undefined, VALEUR1: undefined, VALEUR2: undefined, LISTEVAL: [] })} />
                    </Form.Item>
                    <Form.Item name="OPECOMP" label="Opérateur" rules={[{ required: true, message: 'Opérateur requis' }]}>
                        <Select showSearch optionFilterProp="label" placeholder="Choisir un opérateur…" disabled={!champSel}
                            options={opsPossibles.map(o => ({ value: o.CODOPECOMP, label: `${o.CODOPECOMP} — ${o.LIBCOMP}` }))}
                            onChange={() => f.setFieldsValue({ VALEUR1: undefined, VALEUR2: undefined, LISTEVAL: [] })} />
                    </Form.Item>
                    {isListe && (
                        <Form.Item name="LISTEVAL" label="Valeurs autorisées" rules={[{ required: true, message: 'Au moins une valeur' }]}>
                            {hasDomaine
                                ? <Select mode="multiple" showSearch optionFilterProp="label"
                                    placeholder="Choisir…" options={domainOpts} />
                                : <Select mode="tags" open={false} suffixIcon={null}
                                    placeholder="Saisir une valeur puis Entrée" tokenSeparators={[';', ',', ' ']} />}
                        </Form.Item>
                    )}
                    {!isListe && valeurInput('VALEUR1', isVal2 ? 'Valeur minimale' : 'Valeur')}
                    {!isListe && isVal2 && valeurInput('VALEUR2', 'Valeur maximale')}
                </Form>
                {errSp && <Alert type="error" message={errSp} showIcon style={{ marginTop: 8 }} />}
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                <MuiButton variant="contained" sx={{ bgcolor: "#116599" }} startIcon={<Close />} onClick={p.onClose}>Annuler</MuiButton>
                <MuiButton variant="contained" sx={{ bgcolor: "#116599" }} startIcon={<Save />} disabled={checking} onClick={ok}>
                    {checking ? 'Vérification…' : 'Valider'}
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
};

// ============================ Modale "Condition existante" ============================
// Picker unifie conditions + groupes ACTIFS, recherche code/libelle,
// apercu inline (libelle, type, expression tronquee).
const RefPickerModal = (p: {
    open: boolean;
    ctx: BuilderCtx;
    onClose: () => void;
    onPick: (e: Omit<ElemDraft, '_id'>) => void;
}) => {
    const [q, setQ] = useState('');
    useEffect(() => { if (p.open) setQ(''); }, [p.open]);
    const actif = (x: any) => x.ACTIVE === true || x.ACTIVE === 1;
    const items = [
        ...p.ctx.conditions.filter(actif).map(c => ({
            kind: 'REFC' as const, key: c.CODCOND, code: c.CODCOND,
            lib: c.LIBCOND, type: 'Unique',
            expr: exprCondition(c, p.ctx.operateurs),
            pick: { kind: 'REFC' as const, CODCOND: c.CODCOND },
        })),
        ...p.ctx.groupes.filter(actif).filter(g => g.IDGRP !== p.ctx.excludeIdgrp).map(g => ({
            kind: 'REFG' as const, key: `G${g.IDGRP}`, code: g.CODGRP,
            lib: g.LIBGRP, type: 'Groupe',
            // EXPRCANON : expression canonique complete du groupe
            // (arbre evalue feuille par feuille, stockee en base)
            expr: g.EXPRCANON || `«${g.CODGRP}»`,
            pick: { kind: 'REFG' as const, IDGRPFILS: g.IDGRP },
        })),
    ].filter(i => {
        const s = q.trim().toLowerCase();
        return !s || i.code.toLowerCase().includes(s) || (i.lib || '').toLowerCase().includes(s);
    });
    return (
        <Dialog open={p.open} onClose={p.onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{
                bgcolor: 'var(--primaryColor)', color: 'white', py: 1.5,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <b>Condition existante</b>
                <IconButton onClick={p.onClose} size="small" sx={{ color: 'white' }}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 1.5 }}>
                <Input size="small" allowClear placeholder="Rechercher par code ou libellé…"
                    value={q} onChange={e => setQ(e.target.value)} style={{ marginBottom: 8 }} />
                {items.length === 0 && <Empty description="Aucune règle active" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
                    {items.map(i => (
                        <ListItemButton key={i.key} onClick={() => p.onPick(i.pick)} sx={{ borderRadius: 1 }}>
                            <ListItemText
                                primary={<span><b>{i.lib}</b></span>}
                                secondary={
                                    <>
                                        <Tag color={i.kind === 'REFC' ? 'blue' : 'green'} style={{ marginRight: 4 }}>
                                            {i.type} · Actif
                                        </Tag>
                                        <span style={{ color: '#888' }}>{i.code}</span>
                                        <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--darkColor)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            title={i.expr}>
                                            {i.expr}
                                        </div>
                                    </>
                                } />
                        </ListItemButton>
                    ))}
                </List>
            </DialogContent>
        </Dialog>
    );
};

// ============================ Editeur de composition ============================
// Liste plate : chaque ligne = un element, l'operateur ET/OU sous un element
// le relie au suivant (absent sous le dernier). Les 3 boutons ouvrent les
// modales secondaires ; le resultat ne rejoint le brouillon qu'a "Valider".
const Composer = (p: {
    draft: FlatDraft;
    onChange: (d: FlatDraft) => void;
    depth: number;
    ctx: BuilderCtx;
    // Mode drill-in (modale sous-groupe) : "Sous-groupe" ajoute une ligne
    // et cliquer dessus descend dedans, sans ouvrir une autre modale.
    onDescend?: (i: number) => void;
}) => {
    const [sub, setSub] = useState<{ kind: 'COND' | 'REF' | 'SOUS'; idx: number | null } | null>(null);
    const els = p.draft.elements;
    const drill = !!p.onDescend;

    const upd = (d: FlatDraft) => p.onChange(d);
    const setOp = (i: number, op: Op) =>
        upd({ ...p.draft, ops: p.draft.ops.map((o, j) => j === i ? op : o) });
    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= els.length) return;
        const elements = [...els];
        [elements[i], elements[j]] = [elements[j], elements[i]];
        upd({ ...p.draft, elements });          // les ops restent positionnels
    };
    // Suppression : l'operateur de l'element (vers le suivant) meurt avec lui ;
    // le predecesseur garde son operateur qui relie alors l'element suivant.
    const remove = (i: number) => {
        const elements = els.filter((_, j) => j !== i);
        const ops = [...p.draft.ops];
        if (ops.length) ops.splice(i < els.length - 1 ? i : i - 1, 1);
        upd({ elements, ops });
    };
    const push = (e: Omit<ElemDraft, '_id'>) => {
        const elements = [...els, { ...e, _id: nextId() }];
        const ops = els.length ? [...p.draft.ops, 'ET' as Op] : [];
        upd({ elements, ops });
    };
    const replace = (i: number, e: Omit<ElemDraft, '_id'>) =>
        upd({ ...p.draft, elements: els.map((x, j) => j === i ? { ...e, _id: x._id } : x) });

    // Reference introuvable dans le catalogue (supprimee ou inaccessible) :
    // affichee en rouge pour ne pas masquer une reference invalide.
    const refMissing = (e: ElemDraft) =>
        (e.kind === 'REFC' && !!e.CODCOND && !p.ctx.conditions.some(x => x.CODCOND === e.CODCOND)) ||
        (e.kind === 'REFG' && !!e.IDGRPFILS && !p.ctx.groupes.some(x => x.IDGRP === e.IDGRPFILS));

    const resume = (e: ElemDraft) => {
        if (e.kind === 'COND') return e.cond ? exprInline(e.cond, p.ctx.operateurs) : '…';
        if (e.kind === 'REFC') {
            const c = p.ctx.conditions.find(x => x.CODCOND === e.CODCOND);
            return c ? `${c.CODCOND} — ${c.LIBCOND}` : `${e.CODCOND} — référence introuvable`;
        }
        if (e.kind === 'REFG') {
            const g = p.ctx.groupes.find(x => x.IDGRP === e.IDGRPFILS);
            return g ? `${g.CODGRP} — ${g.LIBGRP}` : (e.IDGRPFILS ? `#${e.IDGRPFILS} — référence introuvable` : '…');
        }
        return e.fils ? flatExpr(e.fils, p.ctx) : '(…)';
    };
    const tagOf = (e: ElemDraft) =>
        refMissing(e) ? <Tag color="error">Introuvable</Tag>
            : e.kind === 'COND' ? <Tag color="blue">Condition simple</Tag>
                : e.kind === 'REFC' ? <Tag color="cyan">Condition existante</Tag>
                    : e.kind === 'REFG' ? <Tag color="green">Groupe</Tag>
                        : <Tag color="purple">Sous-groupe</Tag>;

    const editOf = (e: ElemDraft, i: number) =>
        setSub({ kind: e.kind === 'COND' ? 'COND' : e.kind === 'SOUS' ? 'SOUS' : 'REF', idx: i });

    return (
        <Box>
            {els.length === 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Aucun élément — ajoutez une condition ou un sous-groupe.
                </Typography>
            )}
            {els.map((e, i) => (
                <React.Fragment key={e._id}>
                    <Box sx={{
                        display: 'flex', gap: 1, alignItems: 'center',
                        background: '#fff', border: '1px solid #e4ebf1', borderRadius: 1, p: 1
                    }}>
                        <Typography variant="caption" sx={{ width: 18, color: 'text.secondary' }}>{i + 1}.</Typography>
                        {tagOf(e)}
                        <Box sx={{
                            flex: 1, fontSize: '0.85rem',
                            fontFamily: e.kind === 'REFC' || e.kind === 'REFG' ? 'inherit' : 'monospace',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            cursor: drill && e.kind === 'SOUS' ? 'pointer' : 'default'
                        }}
                            title={drill && e.kind === 'SOUS' ? 'Cliquer pour descendre dans ce sous-groupe' : resume(e)}
                            onClick={drill && e.kind === 'SOUS' ? () => p.onDescend!(i) : undefined}>
                            {resume(e)}
                        </Box>
                        <Space size={0}>
                            {drill && e.kind === 'SOUS'
                                ? <IconButton size="small" title="Descendre dans le sous-groupe"
                                    onClick={() => p.onDescend!(i)}><SubdirectoryArrowRight sx={{ fontSize: 15 }} /></IconButton>
                                : <IconButton size="small" onClick={() => editOf(e, i)}><EditOutlined sx={{ fontSize: 15 }} /></IconButton>}
                            <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpward sx={{ fontSize: 15 }} /></IconButton>
                            <IconButton size="small" disabled={i === els.length - 1} onClick={() => move(i, 1)}><ArrowDownward sx={{ fontSize: 15 }} /></IconButton>
                            <IconButton size="small" color="error" onClick={() => remove(i)}><DeleteOutline sx={{ fontSize: 15 }} /></IconButton>
                        </Space>
                    </Box>
                    {i < els.length - 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.3 }}>
                            <Radio.Group size="small" value={p.draft.ops[i]} onChange={ev => setOp(i, ev.target.value)}>
                                <Radio.Button value="ET">ET</Radio.Button>
                                <Radio.Button value="OU">OU</Radio.Button>
                            </Radio.Group>
                        </Box>
                    )}
                </React.Fragment>
            ))}
            <Space wrap style={{ marginTop: 8 }}>
                <Typography variant="caption" color="text.secondary">Ajouter le prochain élément :</Typography>
                <Button size="small" icon={<PlusOutlined />} onClick={() => setSub({ kind: 'COND', idx: null })}>Condition simple</Button>
                <Button size="small" icon={<PlusOutlined />} onClick={() => setSub({ kind: 'REF', idx: null })}>Condition existante</Button>
                <Button size="small" icon={<PlusOutlined />}
                    onClick={() => {
                        if (!drill) { setSub({ kind: 'SOUS', idx: null }); return; }
                        // Mode drill-in : on ajoute le sous-groupe puis on
                        // descend directement dedans (pas de ligne vide a l'ecran).
                        push({ kind: 'SOUS', fils: emptyFlat() });
                        p.onDescend!(els.length);
                    }}
                    disabled={p.depth >= p.ctx.profMax}
                    title={p.depth >= p.ctx.profMax ? `Profondeur maximale (${p.ctx.profMax}) atteinte` : ''}>
                    Sous-groupe
                </Button>
            </Space>

            {sub?.kind === 'COND' && (
                <CondModal open champs={p.ctx.champs} operateurs={p.ctx.operateurs}
                    onReadDomaine={p.ctx.onReadDomaine}
                    onPrevalider={p.ctx.onPrevalider}
                    initial={sub.idx != null ? els[sub.idx]?.cond : undefined}
                    onClose={() => setSub(null)}
                    onOk={cond => { sub.idx != null ? replace(sub.idx, { kind: 'COND', cond }) : push({ kind: 'COND', cond }); setSub(null); }} />
            )}
            {sub?.kind === 'REF' && (
                <RefPickerModal open ctx={p.ctx}
                    onClose={() => setSub(null)}
                    onPick={pick => { sub.idx != null ? replace(sub.idx, pick) : push(pick); setSub(null); }} />
            )}
            {/* En mode drill-in, un sous-groupe n'ouvre jamais de modale :
                on descend dedans dans la meme modale (breadcrumb). */}
            {!drill && sub?.kind === 'SOUS' && (
                <SousGroupeModal open depth={p.depth + 1} ctx={p.ctx}
                    initial={sub.idx != null ? els[sub.idx]?.fils : undefined}
                    onClose={() => setSub(null)}
                    onOk={fils => { sub.idx != null ? replace(sub.idx, { kind: 'SOUS', fils }) : push({ kind: 'SOUS', fils }); setSub(null); }} />
            )}
        </Box>
    );
};

// ============================ Modale "Sous-groupe" ============================
// Version reduite du meme constructeur : elements + operateurs + apercu.
// Anonyme (pas de code) — c'est une parenthese logique, pas une entite.
// Validable seulement avec au moins 2 elements directs valides.
const validateFlat = (d: FlatDraft, depth: number, ctx: BuilderCtx, path = ''): string | null => {
    if (d.elements.length === 0)
        return depth === 0 ? 'Ajoutez au moins un élément.' : `Sous-groupe ${path} : vide.`;
    if (depth > 0 && d.elements.length < 2)
        return `Sous-groupe ${path} : au moins 2 éléments requis.`;
    for (const [i, e] of d.elements.entries()) {
        const p2 = path ? `${path}.${i + 1}` : `${i + 1}`;
        if (e.kind === 'COND') {
            const c = e.cond;
            if (!c?.CHAMPEV) return `Élément ${p2} : champ manquant.`;
            if (!c?.OPECOMP) return `Élément ${p2} : opérateur manquant.`;
            const op = ctx.operateurs.find(o => o.CODOPECOMP === c.OPECOMP);
            if (op?.REQUIERT_LISTE && !(c.LISTEVAL || []).length) return `Élément ${p2} : liste de valeurs vide.`;
            if (op?.REQUIERT_VAL2 && (c.VALEUR1 == null || c.VALEUR2 == null)) return `Élément ${p2} : bornes requises.`;
            if (!op?.REQUIERT_LISTE && !op?.REQUIERT_VAL2 && c.VALEUR1 == null) return `Élément ${p2} : valeur requise.`;
        }
        if (e.kind === 'REFC' && !e.CODCOND) return `Élément ${p2} : choisir une condition.`;
        if (e.kind === 'REFG' && !e.IDGRPFILS) return `Élément ${p2} : choisir un groupe.`;
        if (e.kind === 'SOUS' && e.fils) {
            const err = validateFlat(e.fils, depth + 1, ctx, p2);
            if (err) return err;
        }
    }
    return null;
};

// Verification a blanc de toutes les conditions inline du brouillon
// (recursive sur les sous-groupes) par la procedure stockee.
const prevaliderFlat = async (d: FlatDraft, ctx: BuilderCtx, path = ''): Promise<string | null> => {
    if (!ctx.onPrevalider) return null;
    for (const [i, e] of d.elements.entries()) {
        const p2 = path ? `${path}.${i + 1}` : `${i + 1}`;
        if (e.kind === 'COND' && e.cond) {
            const r = await ctx.onPrevalider(e.cond);
            if (!r.ok) return `Élément ${p2} : ${r.message || 'condition invalide'}`;
        } else if (e.kind === 'SOUS' && e.fils) {
            const err = await prevaliderFlat(e.fils, ctx, p2);
            if (err) return err;
        }
    }
    return null;
};

const SousGroupeModal = (p: {
    open: boolean;
    depth: number;
    ctx: BuilderCtx;
    initial?: FlatDraft;
    onClose: () => void;
    onOk: (d: FlatDraft) => void;
}) => {
    const [draft, setDraft] = useState<FlatDraft>(emptyFlat());
    const [path, setPath] = useState<number[]>([]);
    const [checking, setChecking] = useState(false);
    const [errSp, setErrSp] = useState<string | null>(null);
    useEffect(() => {
        if (!p.open) return;
        setErrSp(null);
        setPath([]);
        setDraft(p.initial ?? emptyFlat());
    }, [p.open]); // eslint-disable-line react-hooks/exhaustive-deps
    // Niveau courant = noeud pointe par path dans l'arborescence complete.
    const nodeAt = (d: FlatDraft, pth: number[]): FlatDraft =>
        pth.reduce((n, i) => n.elements[i]?.fils ?? emptyFlat(), d);
    const updateAt = (d: FlatDraft, pth: number[], upd: (n: FlatDraft) => FlatDraft): FlatDraft => {
        if (pth.length === 0) return upd(d);
        const [i, ...rest] = pth;
        return {
            ...d,
            elements: d.elements.map((e, j) =>
                j === i && e.fils ? { ...e, fils: updateAt(e.fils, rest, upd) } : e),
        };
    };
    const node = nodeAt(draft, path);
    const err = validateFlat(draft, p.depth, p.ctx);
    const preview = flatExpr(node, p.ctx);
    // Contexte du parent : le noeud courant est remplace par '(…)' dans
    // l'expression du parent pour montrer ou l'on se trouve.
    const parentExpr = useMemo(() => {
        if (path.length === 0) return null;
        const parent = nodeAt(draft, path.slice(0, -1));
        const trou = {
            ...parent,
            elements: parent.elements.map((e, j) =>
                j === path[path.length - 1] && e.fils ? { ...e, fils: emptyFlat() } : e),
        };
        return flatExpr(trou, p.ctx);
    }, [draft, path, p.ctx]); // eslint-disable-line react-hooks/exhaustive-deps
    const ok = async () => {
        setChecking(true);
        const e = await prevaliderFlat(draft, p.ctx);
        setChecking(false);
        if (e) { setErrSp(e); return; }
        p.onOk(draft);
    };
    return (
        <Dialog open={p.open} onClose={p.onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{
                bgcolor: 'var(--primaryColor)', color: 'white', py: 1.5,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <b>Sous-groupe</b>
                <IconButton onClick={p.onClose} size="small" sx={{ color: 'white' }}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {/* Bandeau de navigation : fil d'Ariane cliquable + contexte,
                    visuellement distinct de la zone editable */}
                <Box sx={{
                    mb: 1.5, px: 1.5, py: 1, borderRadius: 1,
                    bgcolor: '#eef4f9', border: '1px solid #c9dce9'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>
                            Chemin :
                        </Typography>
                        <Button size="small" type="text"
                            style={path.length === 0
                                ? { fontWeight: 700, color: 'var(--primaryColor)' } : undefined}
                            onClick={() => setPath([])}>Racine</Button>
                        {/* Les numeros suivent la DESCENTE (niveau parcouru),
                            pas l'indice de l'element dans son parent : le
                            chemin se lit "Racine > n1 > n2 > n3". La cible
                            du clic reste l'indice reel (path.slice). */}
                        {path.map((_, k) => (
                            <span key={k} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <span style={{ margin: '0 2px', color: '#999' }}>›</span>
                                <Button size="small" type="text"
                                    style={k === path.length - 1
                                        ? { fontWeight: 700, color: 'var(--primaryColor)' } : undefined}
                                    onClick={() => setPath(path.slice(0, k + 1))}>
                                    n°{k + 1}
                                </Button>
                            </span>
                        ))}
                    </Box>
                    {path.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" component="div">
                                Vous êtes dans le <b>sous-groupe n°{path.length}</b>
                                {' '}— logique <b>{
                                    node.ops.length === 0 ? 'à définir'
                                        : node.ops.every(o => o === node.ops[0])
                                            ? node.ops[0] : 'ET/OU mixte'
                                }</b> — emplacement dans le parent :
                            </Typography>
                            <code style={{ fontSize: '0.78rem', color: 'var(--darkColor)' }}>
                                {parentExpr}
                            </code>
                        </Box>
                    )}
                </Box>
                <Composer draft={node}
                    onChange={nd => setDraft(d => updateAt(d, path, () => nd))}
                    depth={p.depth + path.length} ctx={p.ctx}
                    onDescend={i => setPath([...path, i])} />
                {preview && (
                    <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px dashed var(--primaryColor)', background: 'var(--lightColor002)' }}>
                        <Typography variant="caption" color="text.secondary">
                            {path.length === 0
                                ? 'Expression du sous-groupe — aperçu provisoire'
                                : `Expression du sous-groupe n°${path.length} — aperçu provisoire`}
                        </Typography>
                        <div><code style={{ fontSize: '0.85rem', color: 'var(--darkColor)' }}>{preview}</code></div>
                    </Box>
                )}
                {err && <Alert type="warning" message={err} showIcon style={{ marginTop: 12 }} />}
                {errSp && <Alert type="error" message={errSp} showIcon style={{ marginTop: 12 }} />}
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                <MuiButton variant="contained" sx={{ bgcolor: "#116599" }} startIcon={<Close />} onClick={p.onClose}>Annuler</MuiButton>
                <MuiButton variant="contained" sx={{ bgcolor: "#116599" }} startIcon={<Save />} disabled={!!err || checking} onClick={ok}>
                    {checking ? 'Vérification…' : 'Valider le sous-groupe'}
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
};

// ============================================================================
const Tab1 = (props: Tab1Props) => {
    const [mForm] = Form.useForm();
    // Saisie locale de la recherche : debounce avant d'appeler le backend
    const [searchInput, setSearchInput] = useState(props.query.search);
    useEffect(() => {
        const t = setTimeout(() => {
            if (searchInput !== props.query.search)
                props.onQuery({ ...props.query, page: 1, search: searchInput });
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps
    // Le bouton "Annuler" du parent reinitialise les filtres : la zone
    // de recherche doit suivre la requete exterieure.
    useEffect(() => { setSearchInput(props.query.search); }, [props.query.search]);
    const [draft, setDraft] = useState<FlatDraft>(emptyFlat());
    const [saving, setSaving] = useState(false);

    const champLib = (cod: string) => props.champs.find(c => c.CODCHAMP === cod)?.LIBCHAMP;
    const ctx: BuilderCtx = {
        conditions: props.conditions, groupes: props.groupes,
        champs: props.champs, operateurs: props.operateurs,
        agences: props.agences,
        profMax: props.profMax, excludeIdgrp: props.editingTree?.IDGRP,
        onPrevalider: props.onPrevalider,
        onReadDomaine: props.onReadDomaine,
    };

    // Initialisation de la modale : edition (pre-remplie) ou creation (vide)
    useEffect(() => {
        if (!props.modalOpen) return;
        if (props.editingRule) {
            if (props.editingRule.TYPE === 'CONDITION') {
                const r = props.editingRule.raw;
                mForm.setFieldsValue({ CODE: r.CODCOND, LIBELLE: r.LIBCOND, ACTIVE: r.ACTIVE === true || r.ACTIVE === 1 });
                setDraft({
                    elements: [{
                        _id: nextId(), kind: 'COND',
                        cond: {
                            CHAMPEV: r.CHAMPEV, OPECOMP: r.OPECOMP,
                            VALEUR1: r.VALEUR1, VALEUR2: r.VALEUR2,
                            LISTEVAL: (r.LISTEVAL || '').split(';').filter(Boolean),
                        }
                    }],
                    ops: [],
                });
            } else if (props.editingTree) {
                mForm.setFieldsValue({ CODE: props.editingTree.CODGRP, LIBELLE: props.editingTree.LIBGRP, ACTIVE: props.editingTree.ACTIVE });
                setDraft(treeToFlat(props.editingTree));
            }
        } else {
            mForm.resetFields();
            mForm.setFieldsValue({ ACTIVE: true });
            setDraft(emptyFlat());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.modalOpen]);

    // ---------- Preview temps reel ----------
    const previewTree = (n: GroupeNode): string => {
        const parts = n.ELEMENTS.map(el => {
            if (el.CODCOND) {
                const c = props.conditions.find(x => x.CODCOND === el.CODCOND);
                return c ? exprCondition(c, props.operateurs) : el.CODCOND;
            }
            if (el.FILS) return previewTree(el.FILS);
            // Condition inline : evaluee directement sur l'element
            if (el.CHAMPEV) return exprCondition(el, props.operateurs);
            const g = props.groupes.find(x => x.IDGRP === el.IDGRPFILS);
            return g ? `«${g.CODGRP}»` : '…';
        });
        return parts.length ? `(${parts.join(` ${n.LOGIQUE} `)})` : '()';
    };

    const preview = useMemo(() => flatExpr(draft, ctx),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [draft, props.operateurs, props.conditions, props.groupes]);

    // Definition normalisee envoyee au simulateur : definie plus bas,
    // apres elemPayload / normElements qu'elle utilise.

    // ---------- Payload normalise ----------
    const elemPayload = (e: ElemDraft, ordre: number): any => ({
        ORDRE: ordre,
        CODCOND: e.kind === 'REFC' ? e.CODCOND! : null,
        IDGRPFILS: e.kind === 'REFG' ? e.IDGRPFILS! : null,
        FILS: e.kind === 'SOUS' && e.fils ? filsPayload(e.fils) : null,
        CHAMPEV: e.kind === 'COND' ? e.cond?.CHAMPEV ?? null : null,
        OPECOMP: e.kind === 'COND' ? e.cond?.OPECOMP ?? null : null,
        VALEUR1: e.kind === 'COND' ? e.cond?.VALEUR1 ?? null : null,
        VALEUR2: e.kind === 'COND' ? e.cond?.VALEUR2 ?? null : null,
        LISTEVAL: e.kind === 'COND' ? (e.cond?.LISTEVAL || []).join(';') || null : null,
    });
    // Noeud logique normalise -> element FILS anonyme (pas de CODGRP)
    const normElements = (n: Norm): any[] =>
        'E' in n ? [elemPayload(n.E, 1)] : n.K.map((k, i) =>
            'E' in k ? elemPayload(k.E, i + 1) : {
                ORDRE: i + 1, CODCOND: null, IDGRPFILS: null,
                CHAMPEV: null, OPECOMP: null, VALEUR1: null, VALEUR2: null, LISTEVAL: null,
                FILS: {
                    IDGRP: 0, CODGRP: null, LIBGRP: null, ANONYME: true,
                    LOGIQUE: k.L, ACTIVE: true,
                    ELEMENTS: normElements(k),
                },
            });
    const filsPayload = (d: FlatDraft): any => {
        const n = norm(d)!;
        return {
            IDGRP: d.idgrp ?? 0, CODGRP: null, LIBGRP: null, ANONYME: true,
            LOGIQUE: 'L' in n ? n.L : 'ET', ACTIVE: true,
            ELEMENTS: normElements(n),
        };
    };

    // Definition normalisee envoyee au simulateur : meme forme que
    // SaveDefinition (LOGIQUE + ELEMENTS discrimines), sans identite.
    const simDefinition = useMemo(() => {
        const els = draft.elements;
        if (!els.length) return null;
        const single = els.length === 1 ? els[0] : null;
        if (single?.kind === 'COND' && single.cond)
            return { LOGIQUE: 'ET', ELEMENTS: [elemPayload(single, 1)] };
        const rootNorm = single?.kind === 'SOUS' && single.fils ? norm(single.fils)! : norm(draft)!;
        return { LOGIQUE: 'L' in rootNorm ? rootNorm.L : 'ET', ELEMENTS: normElements(rootNorm) };
    }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

    // Conversion : le type cible differe de l'entite editee (condition ->
    // groupe ou groupe -> condition). La regle d'origine est TOUJOURS
    // conservee (strategie 3) : la nouvelle est creee avec un code distinct.
    const elsNow = draft.elements;
    const targetIsCond = elsNow.length === 1 && elsNow[0].kind === 'COND';
    const isConverting = !!props.editingRule &&
        ((props.editingRule.TYPE === 'CONDITION' && !targetIsCond) ||
         (props.editingRule.TYPE === 'GROUPE' && targetIsCond));

    const errAlert = (msg: string) =>
        Swal.fire({ title: "Attention", text: msg, icon: "warning" });

    const submit = async () => {
        try { await mForm.validateFields(); } catch { return; }
        const v = mForm.getFieldsValue();
        const err = validateFlat(draft, 0, ctx);
        if (err) { errAlert(err); return; }
        const els = draft.elements;
        const single = els.length === 1 ? els[0] : null;
        // Une reference seule = alias implicite -> refuse (le backend dedoublonne
        // aussi par expression, mais on evite l'aller-retour inutile).
        if (single && (single.kind === 'REFC' || single.kind === 'REFG')) {
            errAlert('Une référence seule créerait un alias : ajoutez un autre élément ou enregistrez une condition simple.');
            return;
        }
        // Le code d'une NOUVELLE regle est normalise en majuscules
        // (creation ou conversion : nouveau code requis). En simple
        // modification il est fige et repris tel quel.
        const estNouveauCode = !props.editingRule || isConverting;
        const codeFinal = v.CODE?.trim();
        const codeNorm = estNouveauCode ? codeFinal?.toUpperCase() : codeFinal;

        // Conversion : nouveau code requis, distinct et unique au catalogue.
        if (isConverting) {
            const pris = props.conditions.some(c => c.CODCOND?.toUpperCase() === codeNorm)
                || props.groupes.some(g => g.CODGRP?.toUpperCase() === codeNorm)
                || codeNorm === props.editingRule?.CODE?.toUpperCase();
            if (!codeNorm || pris) {
                errAlert(`La règle d'origine ${props.editingRule?.CODE} sera conservée : saisissez un code distinct et non utilisé pour la nouvelle règle.`);
                return;
            }
        }
        setSaving(true);
        let res: SaveResult;
        // Definition unifiee : ELEMENTS discrimines (inline / CODCOND /
        // IDGRPFILS / FILS anonyme). Le backend determine le type final.
        const def: any = {
            IDGRP: 0,
            CODGRP: codeNorm,
            LIBGRP: v.LIBELLE?.trim(),
            LOGIQUE: 'ET',
            ACTIVE: v.ACTIVE ?? true,
            UTILIS: props.utilis,
            ROWVER: null,
            ANONYME: false,
            ELEMENTS: [] as any[],
        };
        if (single?.kind === 'COND' && single.cond) {
            def.ROWVER = props.editingRule?.TYPE === 'CONDITION' ? (props.editingRule.raw?.ROWVER ?? null) : null;
            def.ELEMENTS = [{
                ORDRE: 1,
                CHAMPEV: single.cond.CHAMPEV,
                OPECOMP: single.cond.OPECOMP,
                VALEUR1: single.cond.VALEUR1 ?? null,
                VALEUR2: single.cond.VALEUR2 ?? null,
                LISTEVAL: (single.cond.LISTEVAL || []).join(';') || null,
            }];
        } else {
            // La racine est le sous-groupe lui-meme si la liste ne contient
            // qu'une parenthese ; sinon on normalise la liste racine.
            const rootNorm = single?.kind === 'SOUS' && single.fils ? norm(single.fils)! : norm(draft)!;
            // En conversion condition -> groupe, le groupe est NOUVEAU
            // (IDGRP=0) : la condition d'origine reste intacte.
            def.IDGRP = props.editingRule?.TYPE === 'GROUPE' ? (props.editingTree?.IDGRP ?? 0) : 0;
            def.LOGIQUE = 'L' in rootNorm ? rootNorm.L : 'ET';
            def.ROWVER = props.editingRule?.TYPE === 'GROUPE' ? (props.editingTree?.ROWVER ?? null) : null;
            def.ELEMENTS = normElements(rootNorm);
            if (props.editingRule?.TYPE === 'CONDITION') {
                // Si l'element inline est reste identique a la condition
                // d'origine, il devient une REFERENCE vivante vers elle.
                const raw = props.editingRule.raw;
                const idx = def.ELEMENTS.findIndex((e: any) => e.CHAMPEV != null
                    && e.CHAMPEV === raw.CHAMPEV && e.OPECOMP === raw.OPECOMP
                    && (e.VALEUR1 ?? null) === (raw.VALEUR1 ?? null)
                    && (e.VALEUR2 ?? null) === (raw.VALEUR2 ?? null)
                    && (e.LISTEVAL ?? null) === (raw.LISTEVAL ?? null));
                if (idx >= 0) {
                    def.ELEMENTS[idx] = { ORDRE: def.ELEMENTS[idx].ORDRE, CODCOND: props.editingRule.CODE };
                }
            }
        }
        res = await props.onSaveDefinition({
            // ISEDIT = on modifie une regle du MEME type ; en conversion,
            // la nouvelle entite est creee et l'originale conservee.
            ISEDIT: targetIsCond
                ? props.editingRule?.TYPE === 'CONDITION'
                : props.editingRule?.TYPE === 'GROUPE',
            DEFINITION: def,
        }, !!props.editingRule);
        setSaving(false);
        if (!res.ok)
            Swal.fire({ title: "Erreur", text: res.detail ? `${res.message} : ${res.detail}` : (res.message || 'Erreur inconnue'), icon: "error" });
    };

    // Remplit les champs en lecture seule du detail a chaque selection
    // (et quand l'arbre d'un groupe finit de charger).
    useEffect(() => {
        const r = props.selectedRule;
        if (!r) { props.form.resetFields(); return; }
        const base = {
            D_CODE: r.CODE,
            D_LIB: r.LIBELLE,
            D_TYPE: r.TYPE === 'GROUPE' ? 'Groupe' : 'Unique',
            D_STATUT: r.ACTIVE ? 'Activé' : 'Désactivé',
        };
        if (r.TYPE === 'CONDITION') {
            const c = r.raw;
            const op = props.operateurs.find(o => o.CODOPECOMP === c.OPECOMP);
            const val = (op?.REQUIERT_LISTE === true || op?.REQUIERT_LISTE === 1)
                ? (c.LISTEVAL || '').split(';').filter(Boolean).join(' ; ')
                : (op?.REQUIERT_VAL2 === true || op?.REQUIERT_VAL2 === 1)
                    ? `${c.VALEUR1 ?? '—'}  et  ${c.VALEUR2 ?? '—'}`
                    : (c.VALEUR1 ?? '—');
            props.form.setFieldsValue({
                ...base,
                D_CHAMP: `${c.CHAMPEV} — ${champLib(c.CHAMPEV) ?? ''}`.replace(/ — $/, ''),
                D_OPE: op ? `${c.OPECOMP} — ${op.LIBCOMP}` : c.OPECOMP,
                D_VALEUR: val,
                D_EXPR: r.EXPRESSION,
            });
        } else {
            // Groupe : agregats calcules depuis l'arbre resolu
            // (champ commun ou "Plusieurs champs", "ET"/"OU"/"ET / OU",
            // "Plusieurs valeurs" si des valeurs existent dans le groupe)
            const tree = props.groupeTree;
            let champ = '…', ope = '…', valeur = '…';
            if (tree) {
                const cods: string[] = [];
                const logs = new Set<string>();
                const collect = (n: GroupeNode) => {
                    logs.add(n.LOGIQUE);
                    n.ELEMENTS.forEach(e => {
                        if (e.CODCOND) cods.push(e.CODCOND);
                        else if (e.FILS) collect(e.FILS);
                    });
                };
                collect(tree);
                const chs = new Set(
                    cods.map(cod => props.conditions.find(c => c.CODCOND === cod)?.CHAMPEV)
                        .filter((x): x is string => !!x)
                );
                const only = [...chs][0];
                champ = chs.size === 0 ? '—'
                    : chs.size === 1 ? `${only} — ${champLib(only) ?? ''}`.replace(/ — $/, '')
                    : 'Plusieurs champs';
                ope = logs.size === 1 ? [...logs][0] : 'ET / OU';
                valeur = cods.some(cod => {
                    const c = props.conditions.find(x => x.CODCOND === cod);
                    return c && (c.VALEUR1 || c.VALEUR2 || c.LISTEVAL);
                }) ? 'Plusieurs valeurs' : '—';
            }
            props.form.setFieldsValue({
                ...base,
                D_CHAMP: champ,
                D_OPE: ope,
                D_VALEUR: valeur,
                D_EXPR: tree ? previewTree(tree) : '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedRule, props.groupeTree]);

    // Champs communs aux deux types ; un groupe ajoute son arborescence
    const detailDefinition = () => {
        return (
            <>
                <InputForm width={5} label="Champ évalué" name="D_CHAMP" type="label" notRequired />
                <InputForm width={5} label="Opérateur" name="D_OPE" type="label" notRequired />
                <InputForm width={5} label="Valeur" name="D_VALEUR" type="label" notRequired />
                <InputForm width={5} label="Expression complète" name="D_EXPR" type="TextArea" disabled row={2} notRequired />
            </>
        );
    };

    // ---------- Table commune ----------
    // Filtrage/pagination cote serveur (ReadRegles) : props.rules contient
    // deja uniquement la page courante filtree.
    const filtered = props.rules;

    // Export des lignes filtrees : le backend (OpeCatalogue/ExportRegles)
    // genere le fichier, le parent le telecharge.
    const handleExport = async (fmt: string) => {
        try { await props.onExport(fmt); }
        catch (e: any) {
            Swal.fire({ title: "Erreur", text: e?.message ?? "Export impossible", icon: "error" });
        }
    };

    return (
        <div className='tabItemAuto' style={{ width: '102%' }}>
            {/* Une seule boite : champs du detail en haut (2 colonnes), table en bas */}
            <Box className='border' sx={{ width: "85%", position: "absolute" }}>
                <legend style={{ color: '#116599' }}> GESTION DES CONDITIONS </legend>

                <Box sx={{ p: 2, mt: -2 }}>
                    {/* 4 champs a gauche, 4 a droite - lecture seule */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6} sx={{ pr: 2 }}>
                            <InputForm width={5} label="Code" name="D_CODE" type="label" notRequired />
                            <InputForm width={5} label="Type" name="D_TYPE" type="label" notRequired />
                            <InputForm width={5} label="Libellé" name="D_LIB" type="label" notRequired />
                            <InputForm width={5} label="Statut" name="D_STATUT" type="label" notRequired />
                        </Grid>
                        <Grid item xs={12} md={6} className='border-left' sx={{ pl: 2 }}>
                            {detailDefinition()}
                        </Grid>
                    </Grid>
                </Box>

                {/* Catalogue commun conditions + groupes */}
                <Box sx={{ p: 2, mt: -2 }}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                        {/* Toolbar : filtres empiles a gauche, recherche + compteur + export a droite.
                            Bordures peu arrondies : radius 4px partout. */}
                        <Box sx={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1,
                            '& .ant-select-selector, & .ant-input':
                                { borderRadius: '3px !important' },
                            // Recherche : l'addon loupe et le champ restent soudes —
                            // arrondi 4px uniquement sur les coins exterieurs
                            '& .ant-input-group-addon': {
                                borderTopLeftRadius: '4px !important',
                                borderBottomLeftRadius: '4px !important',
                                borderTopRightRadius: '0 !important',
                                borderBottomRightRadius: '0 !important'
                            },
                            '& .ant-input-affix-wrapper': {
                                borderTopLeftRadius: '0 !important',
                                borderBottomLeftRadius: '0 !important',
                                borderTopRightRadius: '4px !important',
                                borderBottomRightRadius: '4px !important'
                            }
                        }}>
                            <Stack direction="column" spacing={1}>
                                <TableFilterSelect value={props.query.type}
                                    onChange={v => props.onQuery({ ...props.query, page: 1, type: v as RegleQuery['type'] })}
                                    options={[
                                        { value: 'TOUS', label: 'Tous les types' },
                                        { value: 'CONDITION', label: 'Uniques' },
                                        { value: 'GROUPE', label: 'Groupes' },
                                    ]} />
                                <TableFilterSelect value={props.query.statut}
                                    onChange={v => props.onQuery({ ...props.query, page: 1, statut: v as RegleQuery['statut'] })}
                                    options={[
                                        { value: 'TOUS', label: 'Tous statuts' },
                                        { value: 'ACTIVES', label: 'Activées' },
                                        { value: 'INACTIVES', label: 'Désactivées' },
                                    ]} />
                            </Stack>
                            <Stack direction="column" spacing={1} alignItems="flex-end">
                                <TableSearchInput value={searchInput} onChange={setSearchInput} blueIcon width={220} />
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" color="textSecondary">
                                        {props.total} ligne(s)
                                    </Typography>
                                    <ExportMenuButton
                                        onExport={handleExport}
                                        disabled={props.total === 0}
                                        size="middle"
                                        formats={[
                                            { key: 'pdf', label: 'PDF', icon: <FilePdfOutlined style={{ color: '#d32f2f' }} /> },
                                            { key: 'xlsx', label: 'Excel', icon: <FileExcelOutlined style={{ color: '#107C41' }} /> },
                                            { key: 'csv', label: 'CSV', icon: <FileTextOutlined style={{ color: '#faad14' }} /> },
                                        ]}
                                    />
                                </Stack>
                            </Stack>
                        </Box>
                        <Table
                            rowKey="_key"
                            size="small"
                            columns={props.columns}
                            dataSource={filtered}
                            loading={props.loading}
                            locale={{ emptyText: <Empty description="Aucune règle enregistrée" /> }}
                            rowSelection={{
                                type: 'radio',
                                selectedRowKeys: props.selectedKey ? [props.selectedKey] : [],
                                onChange: keys => props.onSelectRow(filtered.find(r => r._key === keys[0]) ?? null),
                            }}
                            onRow={r => ({ onClick: () => props.onSelectRow(r), style: { cursor: 'pointer' } })}
                            pagination={{
                                current: props.query.page,
                                pageSize: props.pageSize,
                                total: props.total,
                                showSizeChanger: false,
                                showTotal: t => `${t} règles`,
                                onChange: p => props.onQuery({ ...props.query, page: p }),
                            }}
                            scroll={{ x: 'max-content' }}
                        />
                    </Paper>
                </Box>
            </Box>

            {/* Modale unifiee : code/libelle + composition + apercu */}
            <Dialog open={props.modalOpen} onClose={props.onCloseModal} maxWidth="lg" fullWidth>
                <DialogTitle sx={{
                    bgcolor: 'var(--primaryColor)', color: 'white', py: 1.5,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <b>{props.editingRule ? `Modifier ${props.editingRule.CODE}` : 'Nouvelle condition'}</b>
                    <IconButton onClick={props.onCloseModal} size="small" sx={{ color: 'white' }}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Form form={mForm} layout="vertical">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Form.Item name="CODE" label="Code" rules={[{ required: true, message: 'Code requis' }, { max: 25 }]}>
                                    <Input disabled={!!props.editingRule && !isConverting} maxLength={25}
                                        placeholder={isConverting ? 'Nouveau code' : 'Ex : COND010'} />
                                </Form.Item>
                                {isConverting && (
                                    <Typography variant="caption" color="warning.main">
                                        {props.editingRule?.CODE} sera conservée : choisissez un code distinct.
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Form.Item name="LIBELLE" label="Libellé" rules={[{ required: true, message: 'Libellé requis' }]}>
                                    <Input maxLength={500} placeholder="Ex : Contrôle d'un retrait" />
                                </Form.Item>
                            </Grid>
                        </Grid>

                    <Typography variant="caption" color="text.secondary">Composition de la condition</Typography>
                    <Box sx={{
                        mt: 0.5, p: 1.5, borderRadius: 1,
                        border: '1px solid #c9d8e4', borderLeft: '3px solid var(--primaryColor)',
                        background: '#fafcfe'
                    }}>
                        <Composer draft={draft} onChange={setDraft} depth={0} ctx={ctx} />
                    </Box>

                    <Box sx={{
                        mt: 2, p: 1.5, borderRadius: 1,
                        border: '1px dashed var(--primaryColor)',
                        background: 'var(--lightColor002)'
                    }}>
                        <Typography variant="caption" color="text.secondary">
                            Expression complète — aperçu provisoire, validé par le serveur à l'enregistrement
                        </Typography>
                        <div>
                            <code style={{ fontSize: '0.9rem', color: 'var(--darkColor)' }}>
                                {preview || '(…ajoutez un élément)'}
                            </code>
                        </div>
                        {/* Simulateur : juste sous l'expression, verdict par
                            feuille identique au moteur (VRAI/FAUX/INEVALUABLE) */}
                        <SimuBlock definition={simDefinition} draft={draft} ctx={ctx} onSimuler={props.onSimuler} />
                    </Box>

                    <Form.Item name="ACTIVE" valuePropName="checked" style={{ marginTop: 12, marginBottom: 0 }}>
                        <Checkbox>Règle active</Checkbox>
                    </Form.Item>
                    </Form>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <MuiButton variant="contained" sx={{ bgcolor: "#116599" }} startIcon={<Close />} onClick={props.onCloseModal}>Annuler</MuiButton>
                    <MuiButton variant="contained" sx={{ bgcolor: "#116599" }} startIcon={<Save />} disabled={saving} onClick={submit}>
                        Valider et enregistrer
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Tab1;
